export const ID_PARAMS = [
  ":appointmentId",
  ":canvasAppointmentId",
  ":chatId",
  ":dayIndex",
  ":episodeOfCareId",
  ":evaluationNoteId",
  ":exerciseId",
  ":patientId",
  ":referralId",
];

export const ROUTES_PATHS = {
  ROOT: "/",
  PATIENT_DASHBOARD: "/dashboard",
};

export const RMH_PATIENT_PATHS = {
  // PROM path
  PROM: "/prom/:episodeOfCareId",
  // Patient Dashboard Paths
  PATIENT_DASHBOARD: "/dashboard",
  PATIENT_EXERCISES: "/dashboard/exercises",
  PATIENT_EXERCISE_DETAIL: "/dashboard/exercises/:dayIndex/:exerciseId",

  PATIENT_EXERCISE_PLAN: "/dashboard/exercise-plan",
  PATIENT_SESSION: "/dashboard/session/:appointmentId",
  PATIENT_CHAT: "/dashboard/chat",
  PATIENT_CHAT_CONVERSATION: "/dashboard/chat/:chatId",
  PATIENT_VISIT_EDIT: "/dashboard/visits/edit/:appointmentId",
  PATIENT_VISIT: "/dashboard/visits",
  PATIENT_CONDITIONS: "/dashboard/conditions",
  PATIENT_CONDITIONS_CARE_PLAN:
    "/dashboard/conditions/:episodeOfCareId/careplan",
  PATIENT_CONDITIONS_NOTES: "/dashboard/conditions/:episodeOfCareId/notes",
  PATIENT_NEW_VISIT: "/dashboard/new-visit",
  PATIENT_FOLLOW_UP_INIT: "/dashboard/follow-up",
  PATIENT_FOLLOW_UP: "/dashboard/follow-up/:episodeOfCareId",
  // Patient Setting Paths
  PATIENT_SETTING_INIT: "/dashboard/settings",
  PATIENT_PROFILE_SETTINGS: "/dashboard/settings/profile",
  PATIENT_LANGUAGE_SETTINGS: "/dashboard/settings/language",

  PATIENT_FORMS: "/dashboard/forms",

  // Patient Portal v2 Paths
  PORTAL: "/portal",
  PORTAL_VISITS: "/portal/visits",
  PORTAL_VISIT_CALL: "/portal/visits/:canvasAppointmentId/call",
  PORTAL_CHAT: "/portal/chat",
  PORTAL_CHAT_CONVERSATION: "/portal/chat/:chatId",
  PORTAL_CARE_PLAN: "/portal/care-plan",
  PORTAL_SCHEDULE_FOLLOW_UPS: "/portal/followups/:evaluationNoteId",
  PORTAL_BOOK_EVALUATION: "/portal/care-plan/evaluation/new",
  PORTAL_FORMS: "/portal/forms",
  PORTAL_SETTINGS: "/portal/settings",
  PORTAL_PROM: "/portal/prom",
};

export const ONBOARDING_V2_PATHS = {
  SIGN_UP: "/v2/sign-up",
  // `SIGN_IN` is the magic-link screen, and the only way a patient signs in:
  // every "Sign in" link and the middleware's unauthenticated bounce land on
  // it.
  SIGN_IN: "/v2/sign-in",
  SIGN_IN_CONFIRM: "/v2/sign-in/confirm",
  // Password sign-in, kept as an engineer affordance and nothing more: the
  // route 404s outside a `dev_<lastname>` stack (see `isDevEnvironment`) and is
  // linked from no UI, so patients have no path to it. It exists because a
  // personal stack's SES sandbox makes emailing a magic link to yourself
  // awkward, and password reset is gone, so a stack with no password entry
  // would leave seeded accounts unreachable.
  LEGACY_SIGN_IN: "/v2/legacy-sign-in",
  CHECK_EMAIL: "/v2/check-email",
  ELIGIBLE: "/v2/onboard/:patientId/eligible",
  CONSENTS: "/v2/onboard/:patientId/consents",
  CONDITIONS: "/v2/onboard/:patientId/conditions",
  ICE: "/v2/onboard/:patientId/ice",
  INSURANCE: "/v2/onboard/:patientId/insurance",
  APPOINTMENT: "/v2/onboard/:patientId/appointment",
  CONFIRM: "/v2/onboard/:patientId/confirm",
  HEALTH_HISTORY: "/v2/onboard/:patientId/health-history",
};

/**
 * Patient-app routes a super user must never reach while impersonating: every
 * one of them exists to book, move or drop an appointment on the patient's
 * behalf. Kept as its own list (rather than inferred from the path names) so
 * adding a scheduling route is a deliberate decision about impersonation.
 */
export const PATIENT_SCHEDULING_PATHS = [
  RMH_PATIENT_PATHS.PATIENT_NEW_VISIT,
  RMH_PATIENT_PATHS.PATIENT_FOLLOW_UP_INIT,
  RMH_PATIENT_PATHS.PATIENT_FOLLOW_UP,
  RMH_PATIENT_PATHS.PATIENT_VISIT_EDIT,
];

/**
 * Patient-app routes a super user must never reach while impersonating,
 * because the page exists only to write as the patient and so has no
 * meaningful read-only form.
 *
 * Scheduling, plus the PROM questionnaire. The PROM route is blocked here
 * rather than made read-only in the component because it sits outside the
 * dashboard layout and therefore has no impersonation context to consult —
 * and a half-filled survey is not something to show a viewer anyway.
 */
export const IMPERSONATION_BLOCKED_PATHS = [
  ...PATIENT_SCHEDULING_PATHS,
  RMH_PATIENT_PATHS.PROM,
  RMH_PATIENT_PATHS.PORTAL_PROM,
];

/**
 * Routes only a "super user" — a Provider or Admin signed into the patient app
 * via OIDC — can reach. Deliberately kept out of `RMH_PATIENT_PATHS` so the
 * patient middleware's authorized-path list keeps real patients out of them.
 */
export const SUPER_USER_PATHS = {
  IMPERSONATE: "/impersonate",
};

export const RMH_PT_PATHS = {
  // PT Dashboard Paths
  PT_DASHBOARD: "/p-dashboard",
  SESSION: "/session",
  // PT Setting Paths
  PT_PROFILE_SETTINGS: "/p-dashboard/settings/profile",
  PT_NOTIFICATION_SETTINGS: "/p-dashboard/settings/notifications",
  PT_PASSWORD_SETTINGS: "/p-dashboard/settings/password",
  PT_MFA_SETTINGS: "/p-dashboard/settings/mfa",
};

export const ONBOARDING_V2_VALUE_DELIMITER = ":";

export const ERROR_PATHS = {
  GLOBAL_ERROR: "/error",
};

export enum ONBOARDING_V2_STATUS {
  CONSENTS = "CONSENTS",
  CONDITIONS = "CONDITIONS",
  ICE = "ICE",
  ELIGIBLE = "ELIGIBLE",
  INSURANCE = "INSURANCE",
  APPOINTMENT = "APPOINTMENT",
  CONFIRM = "CONFIRM",
  HEALTH_HISTORY = "HEALTH_HISTORY",
  COMPLETED = "COMPLETED",
}
